import { useEffect, useRef, useState, useCallback } from 'react';

// Detect if running locally or on network
const getWsUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'ws://localhost:8080';
  } else if (hostname === '192.168.0.175') {
      return 'ws://192.168.0.175:8080';
  } else {
      // Production
      return `wss://${window.location.host}/ws`;
  }
};

const WS_URL = getWsUrl();

export const useSimpleWebSocket = () => {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const messageHandlers = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);
  
  // Handle incoming messages
  const handleIncomingMessage = useCallback((data) => {
    // console.log('📨 Received:', data.type);
    
    switch (data.type) {
      case 'new_message':
        const handler = messageHandlers.current.get(data.conversation_id);
        if (handler) {
          handler(data.message);
        }
        break;
        
      case 'messages_read':
        // console.log('Messages read by user:', data.reader_id);
        break;
        
      case 'typing':
        // console.log(`User ${data.user_id} is typing`);
        break;
        
      case 'connected':
        // console.log('Connected as user:', data.user_id);
        break;
        
      case 'subscribed':
        // console.log('Subscribed to conversation:', data.conversation_id);
        break;

      case 'new_support_message':
        const supportHandler = messageHandlers.current.get(`support_${data.ticket_id}`);
        if (supportHandler) {
            supportHandler(data.message);
        }
        break;
          
      case 'support_ticket_status':
        const statusHandler = messageHandlers.current.get(`ticket_status_${data.ticket_id}`);
        if (statusHandler) {
            statusHandler(data);
        }
        break;
        
      case 'pong':
        break;
        
      case 'error':
        // console.error('WebSocket error:', data.message);
        break;
        
      default:
        // console.log('Unknown message type:', data.type);
    }
  }, []);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (isConnectingRef.current) {
      // console.log('⚠️ Already connecting, skipping...');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      // console.warn('No JWT token found');
      return;
    }
    
    // Close existing connection
    if (wsRef.current) {
      // console.log('Closing existing connection...');
      wsRef.current.close();
    }
    
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    isConnectingRef.current = true;
    
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
    // console.log('🔌 Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      // console.log('✅ WebSocket connected');
      setIsConnected(true);
      isConnectingRef.current = false;
      
      // Send initial ping
      setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 500);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleIncomingMessage(data);
      } catch (error) {
        // console.error('Failed to parse message:', error);
      }
    };
    
    ws.onclose = (event) => {
      // console.log('🔌 WebSocket disconnected:', event.code, event.reason);
      setIsConnected(false);
      isConnectingRef.current = false;
      
      // Don't reconnect if closed normally (1000) or on purpose
      if (event.code !== 1000 && event.code !== 1001) {
        // Try to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          // console.log('🔄 Attempting to reconnect...');
          connect();
        }, 5000);
      }
    };
    
    ws.onerror = (error) => {
      // console.error('WebSocket error:', error);
      isConnectingRef.current = false;
    };
  }, [handleIncomingMessage]);
  
  // Subscribe to a conversation
  const subscribeToConversation = useCallback((conversationId, onMessage) => {
    // console.log(`📌 Subscribing to conversation ${conversationId}`);
    
    // Store the handler
    messageHandlers.current.set(conversationId, onMessage);
    
    // If connected, send subscription immediately
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe',
          conversation_id: conversationId
        }));
        // console.log(`✅ Subscription sent for conversation ${conversationId}`);
      } catch (error) {
        // console.error('Failed to send subscription:', error);
      }
    }
    
    // Return unsubscribe function
    return () => {
      // console.log(`📌 Unsubscribing from conversation ${conversationId}`);
      messageHandlers.current.delete(conversationId);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({
            type: 'unsubscribe',
            conversation_id: conversationId
          }));
        } catch (error) {
          // console.error('Failed to send unsubscribe:', error);
        }
      }
    };
  }, []);

  const subscribeToSupportTicket = useCallback((ticketId, onMessage) => {
    const key = `support_${ticketId}`;
    messageHandlers.current.set(key, onMessage);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
            type: 'subscribe_support_ticket',
            ticket_id: ticketId
        }));
    }
    
    return () => {
        messageHandlers.current.delete(key);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'unsubscribe_support_ticket',
                ticket_id: ticketId
            }));
        }
    };
}, []);
  
  // Send typing indicator
  const sendTyping = useCallback((conversationId, isTyping = true) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    
    try {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: conversationId,
        is_typing: isTyping
      }));
    } catch (error) {
      // console.error('Failed to send typing:', error);
    }
  }, []);
  
  // Send ping (keep-alive)
  const sendPing = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    
    try {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    } catch (error) {
      // console.error('Failed to send ping:', error);
    }
  }, []);
  
  // Connect only once on mount
  useEffect(() => {
    connect();
    
    // Set up ping every 25 seconds
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendPing();
      }
    }, 25000);
    
    // Cleanup
    return () => {
      clearInterval(pingInterval);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      
      messageHandlers.current.clear();
      isConnectingRef.current = false;
    };
  }, [connect, sendPing]);
  
  return {
    isConnected,
    subscribeToConversation,
    subscribeToSupportTicket,
    sendTyping,
    reconnect: connect
  };
};